import {
  type PropsWithChildren,
  useEffect,
  useId,
  useRef,
} from "react";

import styles from "./Dialog.module.css";

type DialogProps = PropsWithChildren<{
  title: string;
  eyebrow?: string;
  description?: string;
  onClose: () => void;
  size?: "compact" | "wide";
  /** Feature-owned heading variants keep their accessible name on the dialog. */
  chrome?: "surface" | "bare" | "minimal";
}>;

export function Dialog({
  title,
  eyebrow,
  description,
  onClose,
  size = "compact",
  chrome = "surface",
  children,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const hasVisibleHeader = chrome === "surface";
  const isBare = chrome === "bare";
  const isMinimal = chrome === "minimal";

  useEffect(() => {
    const dialog = dialogRef.current;
    const returnTarget = document.activeElement;

    if (dialog && !dialog.open) {
      dialog.showModal();
    }

    return () => {
      if (dialog?.open) {
        dialog.close();
      }

      if (returnTarget instanceof HTMLElement && returnTarget.isConnected) {
        returnTarget.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${styles[size]} ${isBare ? styles.bareDialog : ""} ${isMinimal ? styles.minimalDialog : ""}`}
      data-chrome={chrome}
      aria-label={!hasVisibleHeader ? title : undefined}
      aria-labelledby={hasVisibleHeader ? titleId : undefined}
      aria-describedby={
        hasVisibleHeader && description ? descriptionId : undefined
      }
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      {isMinimal ? (
        <button
          className={`${styles.close} ${styles.minimalClose}`}
          type="button"
          onClick={onClose}
          aria-label="Sluit dit venster"
        >
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
      <div
        className={`${styles.surface} ${isBare ? styles.bareSurface : ""} ${isMinimal ? styles.minimalSurface : ""}`}
      >
        {!isMinimal ? (
          <button
            className={styles.close}
            type="button"
            onClick={onClose}
            aria-label="Sluit dit venster"
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
        {hasVisibleHeader ? (
          <header className={styles.header}>
            {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </header>
        ) : null}
        <div
          className={`${styles.content} ${isBare ? styles.bareContent : ""} ${isMinimal ? styles.minimalContent : ""}`}
        >
          {children}
        </div>
      </div>
    </dialog>
  );
}
